import { useEffect, useRef, useState } from "react";
import { Loader2, X } from "lucide-react";
import "./pdf-canvas-viewer.css";

export interface PdfCanvasViewerProps {
    /** Raw PDF bytes. Caller fetches with auth before passing. */
    data: ArrayBuffer | Uint8Array | null;
    className?: string;
}

interface RenderTask {
    cancel: () => void;
    promise: Promise<void>;
}

interface CancellableTextLayer {
    cancel: () => void;
    render: () => Promise<void>;
}

/**
 * Renders a PDF to scrollable canvases via pdf.js (legacy build).
 *
 * iOS WebKit cannot display PDFs inside an iframe — it hands the blob off
 * to Quick Look with no Close/Back. Canvas rendering keeps the document
 * inside the app dialog on iPad standalone PWAs.
 *
 * Uses the same pdfjs-dist legacy + Vite ?worker pattern as the form
 * template visual editor (modern build breaks Safari).
 */
export function PdfCanvasViewer({ data, className }: PdfCanvasViewerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pageCount, setPageCount] = useState(0);

    useEffect(() => {
        if (!data) {
            setPageCount(0);
            setError(null);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const renderTasks: RenderTask[] = [];
        const textLayers: CancellableTextLayer[] = [];
        const canvases: HTMLCanvasElement[] = [];

        setLoading(true);
        setError(null);
        setPageCount(0);

        const container = containerRef.current;
        if (container) container.innerHTML = "";

        (async () => {
            try {
                const [pdfjsMod, workerMod] = await Promise.all([
                    import("pdfjs-dist/legacy/build/pdf.mjs"),
                    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker"),
                ]);
                if (cancelled) return;

                const pdfjs = pdfjsMod;
                const PdfWorker = workerMod.default;
                pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();

                const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
                const doc = await pdfjs.getDocument({ data: bytes }).promise;
                if (cancelled) return;

                const numPages = doc.numPages;
                setPageCount(numPages);

                const host = containerRef.current;
                if (!host || cancelled) return;

                const containerWidth = host.clientWidth || host.parentElement?.clientWidth || 800;
                const dpr = Math.min(window.devicePixelRatio || 1, 2);

                for (let pageNum = 1; pageNum <= numPages; pageNum++) {
                    if (cancelled) break;

                    const page = await doc.getPage(pageNum);
                    if (cancelled) break;

                    const baseViewport = page.getViewport({ scale: 1 });
                    const scale = Math.max(0.5, (containerWidth - 16) / baseViewport.width);
                    const viewport = page.getViewport({ scale: scale * dpr });

                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    canvas.style.width = `${viewport.width / dpr}px`;
                    canvas.style.height = `${viewport.height / dpr}px`;
                    canvas.className = "block h-full w-full bg-card shadow-sm";
                    canvas.setAttribute("aria-label", `Page ${pageNum} of ${numPages}`);

                    const pageBox = document.createElement("div");
                    pageBox.className = "relative mx-auto max-w-full";
                    pageBox.style.width = `${viewport.width / dpr}px`;
                    pageBox.style.height = `${viewport.height / dpr}px`;
                    pageBox.appendChild(canvas);

                    const textLayerDiv = document.createElement("div");
                    textLayerDiv.className = "textLayer";
                    textLayerDiv.style.setProperty("--total-scale-factor", String(scale));
                    pageBox.appendChild(textLayerDiv);

                    const wrapper = document.createElement("div");
                    wrapper.className = "flex justify-center py-2 first:pt-0 last:pb-0";
                    wrapper.appendChild(pageBox);
                    host.appendChild(wrapper);
                    canvases.push(canvas);

                    const ctx = canvas.getContext("2d");
                    if (!ctx) continue;

                    const task = page.render({ canvasContext: ctx, viewport, canvas });
                    renderTasks.push(task);

                    // Text layer is a selection/copy overlay, not the visible page — a
                    // failure here shouldn't block the (already-rendering) canvas.
                    const textViewport = page.getViewport({ scale });
                    page.getTextContent()
                        .then((textContent) => {
                            if (cancelled) return;
                            const textLayer = new pdfjs.TextLayer({
                                textContentSource: textContent,
                                container: textLayerDiv,
                                viewport: textViewport,
                            });
                            textLayers.push(textLayer);
                            return textLayer.render();
                        })
                        .catch(() => {
                            /* selectable text is a bonus; the rendered page still works */
                        });

                    await task.promise;
                }
            } catch (err: unknown) {
                if (cancelled) return;
                const name = err && typeof err === "object" && "name" in err ? String(err.name) : "";
                if (name === "RenderingCancelledException") return;
                setError(err instanceof Error ? err.message : "Failed to render PDF");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            for (const task of renderTasks) {
                try {
                    task.cancel();
                } catch {
                    /* ignore */
                }
            }
            for (const textLayer of textLayers) {
                try {
                    textLayer.cancel();
                } catch {
                    /* ignore */
                }
            }
            if (containerRef.current) containerRef.current.innerHTML = "";
        };
    }, [data]);

    return (
        <div className={className ?? "relative flex h-full w-full flex-col"}>
            {loading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/40 text-sm text-muted-foreground">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering PDF…
                </div>
            )}
            {error && !loading && (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-sm text-destructive">
                    <X className="h-5 w-5" />
                    <span>{error}</span>
                    <span className="text-xs text-muted-foreground">Use Download to save the file.</span>
                </div>
            )}
            <div
                ref={containerRef}
                className="h-full w-full overflow-y-auto overflow-x-hidden px-2 py-2"
                aria-busy={loading}
                aria-live="polite"
            />
            {!loading && !error && pageCount > 1 && (
                <div className="border-t bg-card px-3 py-1.5 text-center text-[11px] text-muted-foreground">
                    {pageCount} pages — scroll to read
                </div>
            )}
        </div>
    );
}
